import moment from 'moment-timezone-custom';

import { type NextPage } from 'next';
import React, { useEffect, useState } from 'react';

import DealTable from '@/components/Table/DealTable';
import { CollectionsFilter } from '@/components/UI/filters/CollectionsFilter';
import { HighlightCTA } from '@/components/cards/HighlightCTA';
import SeoHead from '@/components/head/SeoHead';
import PageContainer from '@/components/layout/PageContainer';
import { BUCKET_URL, DATE_FORMATS } from '@/constants/constants';
import { usePageEntrance } from '@/hooks/usePageEntrance';
import { FilterTypes } from '@/models/Filters';
import {
  type DealFragment,
  type Deals_Bool_Exp,
} from '@/utilities/__generated__/graphql';
import {
  DateThemeMap,
  getHumanizedDate,
  getLocalDate,
} from '@/utilities/dateFormatter';
import { loadDeals } from '@/utilities/deals/loadDeals';

const DealsPage: NextPage = () => {
  const [allDeals, setAllDeals] = useState<Partial<DealFragment>[]>([]);
  const [deals, setDeals] = useState<Partial<DealFragment>[]>([]);
  const [randomFeaturedOpportunity, setRandomFeaturedOpportunity] =
    useState<Partial<DealFragment> | null>(null);
  const [loading, setLoading] = useState(true);
  const [filterConditions, setFilterConditions] = useState<
    Record<FilterTypes, Deals_Bool_Exp>
  >({} as Record<FilterTypes, Deals_Bool_Exp>);
  usePageEntrance();

  useEffect(() => {
    const loadAllDeals = async () => {
      const { deals } = await loadDeals({
        date: moment().tz(moment.tz.guess()).format(),
      });
      setAllDeals(deals);
    };

    loadAllDeals();
  }, []);

  useEffect(() => {
    const loadFilteredDeals = async (): Promise<Partial<DealFragment>[]> => {
      const currentTime = moment().tz(moment.tz.guess()).format();
      const { deals } = await loadDeals({
        date: currentTime,
        dealCondition: filterConditions,
      });
      return deals;
    };

    loadFilteredDeals().then((res) => {
      setDeals(res);
      setLoading(false);
    });
  }, [filterConditions]);

  const selectCollectionHandler = (collections: string[]) => {
    const collectionCondition: Deals_Bool_Exp = !collections.length
      ? {}
      : {
          CollectionDeals: {
            Collection: {
              nft_contract_address: {
                _in: collections,
              },
            },
          },
        };
    setFilterConditions((prev) => {
      return { ...prev, [FilterTypes.Collection]: collectionCondition };
    });
  };

  useEffect(() => {
    const featuredDeals = allDeals.filter(
      (deal) =>
        deal.is_featured &&
        deal.bidding_expiration &&
        moment(deal.bidding_expiration).isAfter(moment())
    );
    if (featuredDeals.length > 0) {
      const randomIndex = Math.floor(Math.random() * featuredDeals.length);
      setRandomFeaturedOpportunity(featuredDeals[randomIndex]);
    } else {
      setRandomFeaturedOpportunity(null);
    }
  }, [allDeals]);

  let localBiddingExpDate;
  let humanizedLocalBiddingExpDate;
  let dateEnum;
  let theme;
  let pulse;

  if (randomFeaturedOpportunity) {
    localBiddingExpDate = getLocalDate(
      randomFeaturedOpportunity.bidding_expiration,
      DATE_FORMATS.MMMDYhm
    );
    [humanizedLocalBiddingExpDate, dateEnum] =
      getHumanizedDate(localBiddingExpDate);
    theme = DateThemeMap[dateEnum]?.theme;
    pulse = DateThemeMap[dateEnum]?.pulse;
  }

  return (
    <>
      <SeoHead
        title={'OverpassIP | All Licensing Opportunities'}
        imageURI={'/img/OverPassCover.png'}
        description={`Most people don’t have the resources, time, or connections to truly use
        their web3 IP. Our goal at OverpassIP is to provide holders with easily
        accessible opportunities to leverage their NFT’s IP. With OverpassIP,
        the Web3 user will seamlessly connect with the Web2 brand. Join us in
        revolutionizing IP functionality for years to come.`}
      />
      <PageContainer>
        <div className="enter-el-inherit py-0 font-gt-haptik text-heading-5-mobile font-bold text-black md:px-4 md:text-heading-5">
          Licensing Opportunities
        </div>
        <div className="enter-el-inherit pt-2 text-left text-lg text-neutral-50 md:px-4 md:text-lg ">
          Explore OverpassIP licensing opportunities.
        </div>
        {randomFeaturedOpportunity && (
          <div className="emter-el mt-4 md:mx-3">
            <HighlightCTA
              headline={randomFeaturedOpportunity.title}
              pillPulse={pulse}
              pillTheme={theme}
              pillText={`Submit by ${humanizedLocalBiddingExpDate}`}
              coverImageUrl={`${BUCKET_URL}opportunity/${randomFeaturedOpportunity.id}`}
              lowerText="View Opportunity"
              ctaLink={`/deal/${randomFeaturedOpportunity.id}`}
            />
          </div>
        )}
        <div className="enter-el-inherit awaiting-content w-full pt-8">
          <>
            <DealTable
              emptyCardText={'No Opportunities'}
              deals={deals as DealFragment[]}
              filters={
                <div className="flex space-x-3 overflow-scroll md:pr-4">
                  <CollectionsFilter onSelected={selectCollectionHandler} />
                </div>
              }
              isLoading={loading}
            />
          </>
        </div>
      </PageContainer>
    </>
  );
};
export default DealsPage;
